import { useEffect, useRef, useState } from "react";
import type { LooksView, Mood } from "../ui/view-model";
import { assetFor } from "./assets";
import { SCENE_HEIGHT, SCENE_WIDTH, moodToFrame, renderPetScene } from "./draw";

export interface PetCanvasProps {
  species: string;
  petName?: string;
  mood: Mood;
  reducedMotion: boolean;
  width?: number;
  height?: number;
  scale?: number;
  looks?: LooksView;
  sleeping?: boolean;
}

const ANIMATION_INTERVAL_MS = 350;

export function PetCanvas({
  species,
  petName,
  mood,
  reducedMotion,
  width = SCENE_WIDTH,
  height = SCENE_HEIGHT,
  scale = 1,
  looks,
  sleeping,
}: PetCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [tick, setTick] = useState(0);
  const asset = assetFor(species);
  const effectiveTick = reducedMotion ? 0 : tick;
  const frameName = moodToFrame(asset.class, sleeping ? "resting" : mood, effectiveTick);

  useEffect(() => {
    if (reducedMotion || mood === "dead") {
      return;
    }
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
    }, ANIMATION_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion, mood, sleeping]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    renderPetScene(ctx, {
      width,
      height,
      asset,
      mood,
      tick: effectiveTick,
      reducedMotion,
      scale,
      looks,
      sleeping,
    });
  }, [asset, effectiveTick, height, looks, mood, reducedMotion, scale, sleeping, width]);

  const label = petName ? `${petName} the ${species} in a tank` : `Your ${species} in a tank`;

  return (
    <canvas
      ref={canvasRef}
      id="cv"
      className="pet-tank-canvas"
      width={width}
      height={height}
      role="img"
      aria-label={label}
      data-testid="pet-canvas"
      data-species={species}
      data-mood={mood}
      data-frame={frameName}
      data-sleeping={sleeping ? "true" : undefined}
    />
  );
}
